import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { clientDashboardAPI, timelineAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { cn, formatDate, formatDuration, priorityConfig } from '../lib/utils';
import {
    CheckCircle,
    Circle,
    PlayCircle,
    Sparkles,
    Clock,
    Calendar,
    History,
    Loader2,
    BarChart3,
    ListTodo,
    AlertCircle,
    Timer,
    TrendingUp,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { format, differenceInDays, parseISO, addDays, subDays, startOfDay, endOfDay, eachDayOfInterval, isWithinInterval, isSameDay } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import {
    ComposedChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Bar,
    Legend,
} from 'recharts';

const statusColors = {
    'to do': '#9FB0AC',
    'in progress': '#6EC1E4',
    'review': '#F5C26B',
    'done': '#7ED957',
    'complete': '#7ED957',
};

const MONTH_NAMES = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

// Custom tooltip for the S-Curve chart
const SCurveTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0]?.payload;
    if (!data) return null;

    return (
        <div className="bg-white border border-brand-border rounded-lg shadow-lg p-3 text-sm">
            <p className="font-semibold text-brand-text-primary mb-1">
                {format(parseISO(data.date), 'd MMMM yyyy', { locale: idLocale })}
            </p>
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#2F6F63' }} />
                    <span className="text-brand-text-muted">Kumulatif:</span>
                    <span className="font-semibold text-brand-text-primary">{data.cumulative}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#6EC1E4' }} />
                    <span className="text-brand-text-muted">Harian:</span>
                    <span className="font-semibold text-brand-text-primary">{data.daily}</span>
                </div>
            </div>
        </div>
    );
};

// S-Curve Chart component
const SCurveChart = ({ spaceId }) => {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!spaceId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await clientDashboardAPI.getStatTimeline(spaceId, year, month);
            setData(res.data?.data || []);
        } catch (err) {
            console.error('Failed to load S-curve data:', err);
            setError('Gagal memuat data kurva S');
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [spaceId, year, month]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePrevMonth = () => {
        if (month === 1) {
            setMonth(12);
            setYear(y => y - 1);
        } else {
            setMonth(m => m - 1);
        }
    };

    const handleNextMonth = () => {
        if (month === 12) {
            setMonth(1);
            setYear(y => y + 1);
        } else {
            setMonth(m => m + 1);
        }
    };

    const maxCumulative = data.length > 0 ? Math.max(...data.map(d => d.cumulative)) : 0;
    const maxDaily = data.length > 0 ? Math.max(...data.map(d => d.daily)) : 0;
    const totalDone = data.length > 0 ? data[data.length - 1]?.cumulative || 0 : 0;

    return (
        <Card className="border-brand-border shadow-card">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-brand-primary" />
                        Kurva S - Progress Bulanan
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handlePrevMonth}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm font-medium text-brand-text-primary min-w-[140px] text-center">
                            {MONTH_NAMES[month - 1]} {year}
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleNextMonth}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex items-center justify-center h-[300px]">
                        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-[300px] text-brand-text-muted">
                        <AlertCircle className="w-8 h-8 mb-2 text-brand-error" />
                        <p className="text-sm">{error}</p>
                        <Button variant="outline" size="sm" className="mt-2" onClick={fetchData}>
                            Coba Lagi
                        </Button>
                    </div>
                ) : data.length === 0 || maxCumulative === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[300px] text-brand-text-muted">
                        <BarChart3 className="w-10 h-10 mb-3 opacity-40" />
                        <p className="text-sm font-medium">Belum ada data untuk bulan ini</p>
                        <p className="text-xs mt-1">Task yang selesai akan muncul di kurva S</p>
                    </div>
                ) : (
                    <>
                        {/* Summary row */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="p-3 bg-brand-background rounded-lg text-center">
                                <div className="text-xl font-bold text-brand-primary">{totalDone}</div>
                                <div className="text-xs text-brand-text-muted">Total Selesai</div>
                            </div>
                            <div className="p-3 bg-brand-background rounded-lg text-center">
                                <div className="text-xl font-bold text-brand-text-primary">{maxDaily}</div>
                                <div className="text-xs text-brand-text-muted">Maks. Harian</div>
                            </div>
                            <div className="p-3 bg-brand-background rounded-lg text-center">
                                <div className="text-xl font-bold text-brand-text-secondary">
                                    {data.length > 0 ? (totalDone / data.length).toFixed(1) : 0}
                                </div>
                                <div className="text-xs text-brand-text-muted">Rata-rata/Hari</div>
                            </div>
                        </div>

                        {/* Chart */}
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    data={data}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                >
                                    <defs>
                                        <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#2F6F63" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#2F6F63" stopOpacity={0.02} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#e5e7eb"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11, fill: '#9FB0AC' }}
                                        tickLine={false}
                                        axisLine={{ stroke: '#e5e7eb' }}
                                        tickFormatter={(val) => {
                                            try {
                                                return format(parseISO(val), 'd');
                                            } catch {
                                                return val;
                                            }
                                        }}
                                        interval={Math.max(0, Math.floor(data.length / 10) - 1)}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: '#9FB0AC' }}
                                        tickLine={false}
                                        axisLine={false}
                                        allowDecimals={false}
                                    />
                                    <Tooltip content={<SCurveTooltip />} />
                                    {/* Daily bars */}
                                    <Bar
                                        dataKey="daily"
                                        fill="#6EC1E4"
                                        opacity={0.5}
                                        radius={[2, 2, 0, 0]}
                                        name="Harian"
                                    />
                                    {/* Cumulative area */}
                                    <Area
                                        type="monotone"
                                        dataKey="cumulative"
                                        stroke="#2F6F63"
                                        strokeWidth={2.5}
                                        fill="url(#colorCumulative)"
                                        dot={false}
                                        activeDot={{
                                            r: 5,
                                            stroke: '#2F6F63',
                                            strokeWidth: 2,
                                            fill: '#fff'
                                        }}
                                        name="Kumulatif"
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Legend */}
                        <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-brand-border">
                            <div className="flex items-center gap-2 text-xs">
                                <div className="w-4 h-2 rounded-sm" style={{ backgroundColor: '#2F6F63' }} />
                                <span className="text-brand-text-muted">Kumulatif (Task Selesai)</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <div className="w-4 h-2 rounded-sm" style={{ backgroundColor: '#6EC1E4', opacity: 0.5 }} />
                                <span className="text-brand-text-muted">Harian</span>
                            </div>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

// Timeline Gantt component for Client Dashboard
const TimelineGantt = ({ tasks }) => {
    if (!tasks || tasks.length === 0) return null;

    // Calculate date range
    const allDates = tasks.flatMap(t => [
        t.planned_start,
        t.planned_end,
        t.real_start,
        t.real_end
    ]).filter(Boolean);

    if (allDates.length === 0) return null;

    const minDate = subDays(parseISO(allDates.reduce((a, b) => a < b ? a : b)), 3);
    const maxDate = addDays(parseISO(allDates.reduce((a, b) => a > b ? a : b)), 3);
    const days = eachDayOfInterval({ start: minDate, end: maxDate });
    const totalDays = days.length;
    const dayWidth = Math.max(30, 800 / totalDays);

    const getBarStyle = (start, end, color, type = 'planned') => {
        if (!start || !end) return null;
        const startDate = parseISO(start);
        const endDate = parseISO(end);
        const startOffset = differenceInDays(startDate, minDate);
        const duration = Math.max(1, differenceInDays(endDate, startDate) + 1);
        
        return {
            left: `${startOffset * dayWidth}px`,
            width: `${duration * dayWidth}px`,
            backgroundColor: color,
            opacity: type === 'real' ? 1 : 0.4,
        };
    };

    return (
        <div className="overflow-hidden">
            <ScrollArea className="w-full">
                <div style={{ minWidth: `${totalDays * dayWidth + 200}px` }}>
                    {/* Header */}
                    <div className="flex border-b border-brand-border mb-2">
                        <div className="w-[200px] flex-shrink-0 p-2 font-medium text-sm text-brand-text-muted">Task</div>
                        <div className="flex-1 flex">
                            {days.map((day, idx) => (
                                <div
                                    key={idx}
                                    style={{ width: `${dayWidth}px` }}
                                    className={cn(
                                        "text-center text-xs py-1 border-l border-brand-border",
                                        isSameDay(day, new Date()) && "bg-brand-primary/10"
                                    )}
                                >
                                    {format(day, 'd')}
                                    <div className="text-[10px] text-brand-text-muted">
                                        {format(day, 'MMM')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Task rows */}
                    {tasks.slice(0, 10).map((task, idx) => (
                        <div key={task.task_id || idx} className="flex items-center border-b border-brand-border/50 hover:bg-brand-accent-soft/20">
                            <div className="w-[200px] flex-shrink-0 p-2 truncate text-sm">
                                <span className="text-brand-text-muted text-xs">{task.task_code}</span>
                                <p className="truncate" title={task.title}>{task.title}</p>
                            </div>
                            <div className="flex-1 relative h-10">
                                {/* Planned bar */}
                                {task.planned_start && task.planned_end && (
                                    <div
                                        className="absolute top-1 h-3 rounded-sm"
                                        style={getBarStyle(task.planned_start, task.planned_end, '#9FB0AC', 'planned')}
                                        title={`Planned: ${task.planned_start} - ${task.planned_end}`}
                                    />
                                )}
                                {/* Real bar */}
                                {task.real_start && (
                                    <div
                                        className="absolute top-5 h-3 rounded-sm"
                                        style={getBarStyle(task.real_start, task.real_end || format(new Date(), 'yyyy-MM-dd'), statusColors[task.status?.toLowerCase()] || '#6EC1E4', 'real')}
                                        title={`Actual: ${task.real_start} - ${task.real_end || 'ongoing'}`}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                    
                    {tasks.length > 10 && (
                        <div className="p-3 text-center text-sm text-brand-text-muted">
                            +{tasks.length - 10} more tasks
                        </div>
                    )}
                </div>
                <ScrollBar orientation="horizontal" />
            </ScrollArea>
            
            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-brand-border">
                <div className="flex items-center gap-2 text-xs">
                    <div className="w-4 h-2 rounded-sm bg-gray-400/40"></div>
                    <span className="text-brand-text-muted">Planned</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    <div className="w-4 h-2 rounded-sm" style={{ backgroundColor: statusColors['in progress'] }}></div>
                    <span className="text-brand-text-muted">In Progress</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    <div className="w-4 h-2 rounded-sm" style={{ backgroundColor: statusColors['done'] }}></div>
                    <span className="text-brand-text-muted">Done</span>
                </div>
            </div>
        </div>
    );
};

const TaskCard = ({ task, showNewBadge = false }) => {
    const getStatusColor = (status) => {
        const lower = status?.toLowerCase();
        return statusColors[lower] || '#9FB0AC';
    };

    return (
        <div className="p-4 bg-white rounded-lg border border-brand-border hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-brand-text-muted">{task.task_code}</span>
                    {showNewBadge && task.is_new && (
                        <span className="px-2 py-0.5 text-xs bg-brand-primary text-white rounded-full flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            New
                        </span>
                    )}
                </div>
                <span 
                    className="px-2 py-0.5 text-xs rounded-full"
                    style={{ 
                        backgroundColor: `${getStatusColor(task.status)}20`,
                        color: getStatusColor(task.status)
                    }}
                >
                    {task.status}
                </span>
            </div>
            <h4 className="font-medium text-brand-text-primary mb-2 line-clamp-2">{task.title}</h4>
            <div className="flex items-center gap-4 text-xs text-brand-text-muted">
                {task.due_date && (
                    <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(task.due_date)}
                    </div>
                )}
                {task.time_estimate && (
                    <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Est: {formatDuration(task.time_estimate)}
                    </div>
                )}
            </div>
            
            {/* Status History */}
            {task.status_history && task.status_history.length > 0 && (
                <div className="mt-3 pt-3 border-t border-brand-border">
                    <div className="flex items-center gap-1 text-xs text-brand-text-muted mb-2">
                        <History className="w-3 h-3" />
                        Status History
                    </div>
                    <div className="space-y-1">
                        {task.status_history.slice(-3).map((entry, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs">
                                <span 
                                    className="px-1.5 py-0.5 rounded"
                                    style={{ 
                                        backgroundColor: `${getStatusColor(entry.status)}20`,
                                    }}
                                >
                                    {entry.status}
                                </span>
                                <span className="text-brand-text-muted">
                                    {formatDate(entry.changed_at)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const ClientDashboardPage = () => {
    const { spaceId } = useParams();
    const { spaces, currentWorkspace } = useWorkspace();
    const [dashboardData, setDashboardData] = useState(null);
    const [timelineData, setTimelineData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('complete');  // Default to "Complete" tab

    const currentSpace = spaces.find(s => s.id === spaceId);

    useEffect(() => {
        if (spaceId) {
            loadDashboard();
            loadTimeline();
        } else {
            setLoading(false);
        }
    }, [spaceId]);

    const loadDashboard = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await clientDashboardAPI.get(spaceId);
            setDashboardData(res.data);
        } catch (error) {
            console.error('Failed to load dashboard:', error);
            setError(error.response?.data?.detail || 'Gagal memuat dashboard');
        } finally {
            setLoading(false);
        }
    };

    const loadTimeline = async () => {
        try {
            const res = await timelineAPI.getSpaceTimeline(spaceId);
            setTimelineData(res.data);
        } catch (error) {
            console.error('Failed to load timeline:', error);
        }
    };

    // Show loading state
    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    // Show error state
    if (error) {
        return (
            <Layout>
                <div className="p-6 text-center">
                    <AlertCircle className="w-12 h-12 text-brand-error mx-auto mb-4" />
                    <p className="text-brand-error">{error}</p>
                    <Button 
                        onClick={loadDashboard} 
                        variant="outline" 
                        className="mt-4"
                    >
                        Coba Lagi
                    </Button>
                </div>
            </Layout>
        );
    }

    // No space selected state
    if (!spaceId) {
        return (
            <Layout>
                <div className="p-6 text-center text-brand-text-muted">
                    <ListTodo className="w-12 h-12 mx-auto mb-4 text-brand-text-muted" />
                    <p>Pilih space dari sidebar untuk melihat dashboard client</p>
                </div>
            </Layout>
        );
    }

    // Dashboard data not loaded - should not happen if spaceId exists
    if (!dashboardData) {
        return (
            <Layout>
                <div className="p-6 text-center text-brand-text-muted">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-brand-primary" />
                    <p>Memuat dashboard...</p>
                </div>
            </Layout>
        );
    }

    const { summary, tasks } = dashboardData;
    const completionRate = summary.completion_rate || 0;
    const timeProgress = summary.total_estimate_minutes > 0 
        ? (summary.total_logged_minutes / summary.total_estimate_minutes) * 100 
        : 0;

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-heading font-bold text-brand-text-primary">
                        {dashboardData.space_name} - Client Dashboard
                    </h1>
                    <p className="text-sm text-brand-text-muted">
                        Project overview and progress tracking
                    </p>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <Card className="border-brand-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-brand-success/10 flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-brand-success" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-brand-text-primary">{summary.complete_count}</p>
                                    <p className="text-xs text-brand-text-muted">Completed</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <Circle className="w-5 h-5 text-gray-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-brand-text-primary">{summary.not_active_count || 0}</p>
                                    <p className="text-xs text-brand-text-muted">Not Active</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-brand-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-brand-text-primary">{completionRate.toFixed(0)}%</p>
                                    <p className="text-xs text-brand-text-muted">Completion Rate</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Timeline Gantt Chart */}
                {timelineData.length > 0 && (
                    <Card className="border-brand-border shadow-card mb-6">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-brand-primary" />
                                Timeline
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <TimelineGantt tasks={timelineData} />
                        </CardContent>
                    </Card>
                )}

                {/* Task Lists */}
                <Card className="border-brand-border shadow-card">
                    <CardContent className="p-0">
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <div className="border-b border-brand-border px-4">
                                <TabsList className="bg-transparent h-auto p-0">
                                    <TabsTrigger 
                                        value="complete" 
                                        className="tab data-[state=active]:tab-active data-[state=active]:border-brand-success"
                                    >
                                        <CheckCircle className="w-4 h-4 mr-2 text-brand-success" />
                                        Complete ({summary.complete_count})
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="not-active" 
                                        className="tab data-[state=active]:tab-active data-[state=active]:border-gray-500"
                                    >
                                        <Circle className="w-4 h-4 mr-2 text-gray-500" />
                                        Not Active ({summary.not_active_count || 0})
                                    </TabsTrigger>
                                </TabsList>
                            </div>

                            <div className="p-4">
                                <TabsContent value="complete" className="mt-0">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {tasks.complete.length > 0 ? (
                                            tasks.complete.map(task => (
                                                <TaskCard key={task.id} task={task} />
                                            ))
                                        ) : (
                                            <div className="col-span-full text-center py-8 text-brand-text-muted">
                                                No completed tasks yet
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>

                                <TabsContent value="not-active" className="mt-0">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {tasks.not_active && tasks.not_active.length > 0 ? (
                                            tasks.not_active.map(task => (
                                                <TaskCard key={task.id} task={task} />
                                            ))
                                        ) : (
                                            <div className="col-span-full text-center py-8 text-brand-text-muted">
                                                No tasks in Not Active state
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>
                            </div>
                        </Tabs>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};

export default ClientDashboardPage;
